import type { ContactSummary } from "./contactSummary";

/**
 * ONE STORE FOR "WHO IS THIS PHONE", SHARED BY EVERY SURFACE ON THE SCREEN.
 *
 * ── What it replaces ────────────────────────────────────────────────────────────────
 *
 * `useLegacyContactsBook().allContacts`: the caller's entire book, downloaded into the browser so that a
 * call log, a calendar card or a signature list could do `allContacts.find(byPhone)`. One
 * account in the fleet holds 92,069 contacts; a page of calls names a few dozen of them.
 *
 * This store holds only the phones something on screen has asked about, each answered by
 * `POST /contacts/lookup` (see `useContactsLookup`, which is the only way in for UI code).
 *
 * ── Three states, and the difference between two of them is the whole design ────────
 *
 *   undefined   nobody has an answer yet           → render the phone, claim nothing
 *   null        answered: NOT in the caller's book → may offer "add to contacts"
 *   summary     answered: this is the contact
 *
 * A request that FAILS releases its ids back to `undefined`. It must never record `null`:
 * "not in your book" is an affirmative statement that screens act on — they offer to create
 * the contact — and a network blip is not evidence for it.
 *
 * ── Why a module store and not a context ────────────────────────────────────────────
 *
 * Every reader is a leaf (a table cell, a chip, a card header), and the point is that two
 * leaves showing the same phone share one answer and one request. Same reasoning, and the
 * same shape, as `hooks/crm/contactHandlerStore.ts`.
 *
 * ── Freshness ───────────────────────────────────────────────────────────────────────
 *
 * An answer is shown for as long as it is held, and RE-ASKED once it is older than
 * {@link STALE_AFTER_MS} and a reader asks again (a screen mounting, a page of rows
 * changing). The stale value keeps rendering while the new one is fetched. What that buys:
 * a "not in your book" cannot outlive, by more than a navigation, the phone call that just
 * created the contact on the server.
 *
 * Writes made in THIS browser do not wait for that — `useContactWrites` writes through
 * (`put` / `patch` / `markAbsent`), so a rename shows at once on every surface.
 *
 * ── It belongs to ONE account ───────────────────────────────────────────────────────
 *
 * `bindAccount` drops everything when the signed-in user changes. There is no sign-out hook
 * to rely on — a store that is cleared "on logout" is cleared only on the logout paths
 * someone remembered — so the store is stamped with its owner and every reader binds it
 * before reading. An answer for the previous account that lands late is discarded by its
 * `session`.
 *
 * Pure: no React, no network, an injected clock. `createContactLookupStore` exists for the
 * tests; the app uses the one instance exported at the bottom.
 */

/** An answered lookup. `undefined` (no entry) is "not answered", which is not a value. */
export type ContactLookupValue = ContactSummary | null;

/** How long an answer is trusted before the next reader re-asks. See the header. */
export const STALE_AFTER_MS = 60 * 1000;

type Entry = { value: ContactLookupValue; at: number };

export type ContactLookupStore = {
  /** Idempotent. A CHANGE of account drops every answer and starts a new session. */
  bindAccount: (account: string | null) => void;
  /** Identifies the bound account's lifetime. Capture it when asking; pass it back with the answer. */
  session: () => number;
  /** Moves when {@link ContactLookupStore.invalidate} runs — mounted readers re-ask on a change. */
  epoch: () => number;
  read: (contactId: string) => ContactLookupValue | undefined;
  /**
   * A request for this id is out right now. With `read`, this is what tells "still being
   * asked" from "was asked, and the request failed" — both are `undefined` to `read`.
   */
  isInFlight: (contactId: string) => boolean;
  /**
   * Which of these ids need asking — unknown or stale, and not already being asked. Marks
   * them in flight in the same pass, so two surfaces rendering one phone issue one request.
   */
  claim: (contactIds: readonly string[]) => string[];
  /**
   * The answer to a claim. Every id asked about is settled: found → its summary, not found
   * → `null`. Ids that are no longer in flight are skipped — a write-through landed while
   * the request was out, and it is newer than the answer.
   */
  record: (session: number, askedIds: readonly string[], found: readonly ContactSummary[]) => void;
  /**
   * A claim that failed. The ids go back to "not answered" (or stay stale), never to `null`.
   * Readers are told, so one that was waiting for the answer can stop waiting.
   */
  release: (session: number, askedIds: readonly string[]) => void;
  /** Write-through of a row the server just returned (a create, an update). */
  put: (summary: ContactSummary) => void;
  /** The same for a page of rows the server just returned (a search) — one notification. */
  putMany: (summaries: readonly ContactSummary[]) => void;
  /**
   * Write-through of a partial edit made by a browser-direct writer. Merges into an answer
   * that is already held and does nothing otherwise: a patch is a few fields, and minting an
   * entry out of it would show a contact with a tag and no name.
   */
  patch: (contactId: string, fields: Partial<ContactSummary>) => void;
  /** Write-through of a delete. */
  markAbsent: (contactIds: readonly string[]) => void;
  /**
   * Everything held is now suspect (an import, a bulk edit, a delete the server resolved
   * from a filter). Values keep rendering; every mounted reader re-asks.
   */
  invalidate: () => void;
  subscribe: (listener: () => void) => () => void;
};

export const createContactLookupStore = (now: () => number = Date.now): ContactLookupStore => {
  let boundAccount: string | null = null;
  let currentSession = 0;
  let currentEpoch = 0;
  const entries = new Map<string, Entry>();
  const inFlight = new Set<string>();
  const subscribers = new Set<() => void>();

  const notify = (): void => subscribers.forEach((listener) => listener());

  /** A local write settles the id: whatever request is out for it is older than this. */
  const settle = (contactId: string, value: ContactLookupValue): void => {
    inFlight.delete(contactId);
    entries.set(contactId, { value, at: now() });
  };

  return {
    bindAccount: (account) => {
      const next = account?.trim().toLowerCase() || null;
      if (next === boundAccount) return;
      boundAccount = next;
      currentSession += 1;
      entries.clear();
      inFlight.clear();
      // No `notify()`: this runs while a reader is rendering, and every reader re-renders on
      // the same auth change that brought it here.
    },

    session: () => currentSession,
    epoch: () => currentEpoch,

    read: (contactId) => entries.get(contactId)?.value,

    isInFlight: (contactId) => inFlight.has(contactId),

    claim: (contactIds) => {
      const claimed: string[] = [];
      const staleBefore = now() - STALE_AFTER_MS;
      for (const contactId of contactIds) {
        if (!contactId || inFlight.has(contactId)) continue;
        const entry = entries.get(contactId);
        if (entry && entry.at > staleBefore) continue;
        inFlight.add(contactId);
        claimed.push(contactId);
      }
      return claimed;
    },

    record: (session, askedIds, found) => {
      if (session !== currentSession) return;
      const byId = new Map(found.map((summary) => [summary.id, summary]));
      const at = now();
      for (const contactId of askedIds) {
        if (!inFlight.delete(contactId)) continue;
        entries.set(contactId, { value: byId.get(contactId) ?? null, at });
      }
      notify();
    },

    release: (session, askedIds) => {
      if (session !== currentSession) return;
      let released = false;
      for (const contactId of askedIds) released = inFlight.delete(contactId) || released;
      if (released) notify();
    },

    put: (summary) => {
      settle(summary.id, summary);
      notify();
    },

    putMany: (summaries) => {
      if (summaries.length === 0) return;
      for (const summary of summaries) settle(summary.id, summary);
      notify();
    },

    patch: (contactId, fields) => {
      const held = entries.get(contactId)?.value;
      if (!held) return;
      // The id is the key; a patch that carries one must not be able to move the entry.
      settle(contactId, { ...held, ...fields, id: held.id, contactId: held.contactId });
      notify();
    },

    markAbsent: (contactIds) => {
      if (contactIds.length === 0) return;
      for (const contactId of contactIds) settle(contactId, null);
      notify();
    },

    invalidate: () => {
      for (const entry of entries.values()) entry.at = 0;
      currentEpoch += 1;
      notify();
    },

    subscribe: (listener) => {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    }
  };
};

/** The app's one instance. */
export const contactLookupStore = createContactLookupStore();
